import { Box, Card, CardContent, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Control,
  UseFormSetValue,
  FieldErrors,
  UseFormGetValues,
  UseFormTrigger,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddressAutocompleteField, DistanceSlider } from '@/shared/components/forms';
import { CreateAlertFormValues } from '../types';

interface AlertLocationSectionProps {
  readonly control: Control<CreateAlertFormValues>;
  readonly setValue: UseFormSetValue<CreateAlertFormValues>;
  readonly errors: FieldErrors<CreateAlertFormValues>;
  readonly getValues: UseFormGetValues<CreateAlertFormValues>;
  readonly trigger: UseFormTrigger<CreateAlertFormValues>;
}

export function AlertLocationSection({
  control,
  setValue,
  errors,
  getValues,
  trigger,
}: AlertLocationSectionProps) {
  const { t } = useTranslation();

  return (
    <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }} data-testid="alert-location-section">
      <CardContent>
        {/* Section header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              bgcolor: 'primary.main',
              borderRadius: 1,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
            }}
          >
            <LocationOnIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('alerts.location.title')}
          </Typography>
        </Box>

        {/* Address autocomplete */}
        <AddressAutocompleteField<CreateAlertFormValues>
          name="address"
          control={control}
          label={t('alerts.location.fieldLabel')}
          placeholder={t('alerts.location.placeholder')}
          required
          error={!!errors.address}
          helperText={errors.address?.message}
          rules={{
            required: t('alerts.location.required'),
            validate: (_value: string | number | boolean | Date | null | undefined) => {
              const lat = getValues('latitude');
              if (lat === undefined || lat === null) {
                return t('alerts.location.invalid');
              }
              return true;
            },
          }}
          onAddressSelected={suggestion => {
            if (suggestion.coordinates) {
              setValue('latitude', suggestion.coordinates.lat, { shouldValidate: true });
              setValue('longitude', suggestion.coordinates.lng, { shouldValidate: true });
              void trigger('address');
            }
          }}
          onCoordinatesChange={(lat, lng) => {
            setValue('latitude', lat as number | undefined);
            setValue('longitude', lng as number | undefined);
            const currentAddress = getValues('address');
            if (typeof currentAddress === 'string' && currentAddress.trim().length > 0) {
              void trigger('address');
            }
          }}
        />

        {/* Distance slider */}
        <Box sx={{ mt: 2 }}>
          <DistanceSlider<CreateAlertFormValues>
            control={control}
            enabledFieldName="distanceEnabled"
            distanceFieldName="maxDistance"
            label={t('alerts.location.distance.label')}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
